import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import moment from 'moment';
import { NodeStats } from './k8s-analyzer-node-status-panel';
import { K8sPod } from './k8s-analyzer-types';

interface SessionUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

export interface SessionProps {
  _id: string;
  owner: string;
  active: boolean;
  status: string;
  statusCode?: number;
  qrcode?: string;
  podId: string;
  nodeName?: string;
  failedAttempts: number;
  gatewayType: string;
  lastSeen: string;
  lastRestart: string;
  connectedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  user?: SessionUser;
}

interface SessionDetailsCubeProps {
  session: SessionProps;
  pods: K8sPod[];
}

const getSessionStatusColor = (status: string | undefined | null) => {
  if (!status || typeof status !== 'string') return '#9e9e9e';
  
  switch (status.toLowerCase()) {
    case 'connected':
      return '#4caf50'; // green
    case 'qrcode':
      return '#2196f3'; // blue
    case 'initializing':
    case 'browser_started':
    case 'navigating':
      return '#ff9800'; // orange
    case 'disconnected':
    case 'unpaired':
      return '#f44336'; // red
    default:
      return '#9e9e9e'; // grey
  }
};

const getPodColor = (podId: string): string => {
  // Generate a consistent hash from the podId
  let hash = 0;
  for (let i = 0; i < podId.length; i++) {
    hash = podId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Convert hash to RGB color with good saturation and lightness
  const h = Math.abs(hash) % 360;  // Hue (0-360)
  const s = 65;  // Saturation (fixed at 65% for consistency)
  const l = 45;  // Lightness (fixed at 45% for readability)
  
  return `hsl(${h}, ${s}%, ${l}%)`;
};

const getNodeNameFromPodName = (podId: string, pods: K8sPod[]): string | undefined => {
  const pod = pods.find(p => p.name === podId);
  return pod?.nodeName;
};

export const SessionDetailsCube: React.FC<SessionDetailsCubeProps> = ({ session, pods }) => {
  const nodeName = React.useMemo(() => {
    if (session.podId) {
      return getNodeNameFromPodName(session.podId, pods);
    }
    return undefined;
  }, [session.podId, pods]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip 
          label={session.status} 
          size="small"
          sx={{ 
            bgcolor: getSessionStatusColor(session.status),
            color: 'white',
            minWidth: '80px'
          }}
        />
        {session.active && (
          <Chip label="Active" size="small" color="success" variant="outlined" />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-all' }}>
        ID: {session._id}
      </Typography>
      {session.user && (
        <>
          <Typography variant="body2" component="div" noWrap>
            {session.user.fullName || `${session.user.firstName} ${session.user.lastName}`}
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary" noWrap>
            {session.user.email}
          </Typography>
        </>
      )}
      {session.lastSeen && (
        <Typography variant="caption" component="div" color="text.secondary">
          Last seen: {moment(session.lastSeen).fromNow()}
        </Typography>
      )}
      {session.podId && (
        <Typography variant="caption" component="div" color="text.secondary">
          Pod: <Box component="span" >{session.podId}</Box>
        </Typography>
      )}
      {nodeName && (
        <Typography variant="caption" component="div" color="text.secondary">
          Node: <Box component="span" sx={{ color: getPodColor(nodeName) }}>{nodeName}</Box>
        </Typography>
      )}
      {session.createdAt && (
        <Typography variant="caption" component="div" color="text.secondary">
          Created: {moment(session.createdAt).fromNow()}
        </Typography>
      )}
      {session.updatedAt && (
        <Typography variant="caption" component="div" color="text.secondary">
          Updated: {moment(session.updatedAt).fromNow()}
        </Typography>
      )}
      {session.lastRestart && (
        <Typography variant="caption" component="div" color="text.secondary">
          Last restart: {moment(session.lastRestart).fromNow()}
        </Typography>
      )}
    </Box>
  );
};

export default SessionDetailsCube; 